// Helper function pour ajouter une notification au récap quotidien
// À importer dans les fichiers qui ont besoin d'ajouter des notifications

import { collection, addDoc, Timestamp } from 'firebase/firestore';

/**
 * Ajoute une notification au récap quotidien
 * @param {Object} params
 * @param {string} params.type - 'new' | 'modified' | 'cancelled'
 * @param {string} params.category - 'room' | 'restaurant' | 'service' | 'train'
 * @param {string} params.restaurantId - 'cafe' | 'laTable' (si category === 'restaurant')
 * @param {Object} params.data - Données spécifiques selon la catégorie
 * 
 * IMPORTANT pour category='restaurant' :
 * - Si le client a une chambre : inclure hotelReservationNumber dans data
 * - Si le client est extérieur : ne pas inclure hotelReservationNumber
 * Cela permet de différencier les clients dans les notifications (icône Bed vs LogIn)
 * 
 * @param {Object} params.db - Instance Firestore (optionnel, pour compatibilité)
 * @returns {Promise<string>} - ID du document créé
 */
export const addDailyNotification = async ({ type, category, restaurantId, data, db: dbParam }) => {
  try {
    // Si db n'est pas fourni en paramètre, essayer de l'importer dynamiquement
    let db = dbParam;
    if (!db) {
      try {
        const { db: importedDb } = await import('../services/firebase');
        db = importedDb;
      } catch {
        try {
          const { db: importedDb } = await import('../services/firebaseConfig');
          db = importedDb;
        } catch (error) {
          console.error('❌ Impossible d\'importer firebase db');
          throw new Error('Firebase db non disponible');
        }
      }
    }
    
    const notificationData = {
      type, // 'new' ou 'modified'
      category, // 'room', 'restaurant', 'service', 'train'
      createdAt: Timestamp.now(),
      processed: false
    };

    // Ajouter restaurantId si c'est une réservation resto
    if (category === 'restaurant' && restaurantId) {
      notificationData.restaurantId = restaurantId;
    }

    // Ajouter les données spécifiques
    notificationData.data = data;

    const docRef = await addDoc(collection(db, 'daily_notifications'), notificationData);
    console.log('✅ Notification ajoutée au récap quotidien:', docRef.id);
    return docRef.id;
  } catch (error) {
    console.error('❌ Erreur ajout notification quotidienne:', error);
    throw error;
  }
};

/**
 * Exemples d'utilisation :
 * 
 * // Nouvelle réservation chambre
 * await addDailyNotification({
 *   type: 'new',
 *   category: 'room',
 *   data: {
 *     reservationNumber: 'R-2024-001',
 *     reservationId: 'abc123',
 *     clientName: 'Jean Dupont',
 *     email: 'jean@example.com',
 *     checkIn: '2024-11-20',
 *     checkOut: '2024-11-22',
 *     roomNumber: '5',
 *     roomType: 'Chambre Double',
 *     price: 250
 *   }
 * });
 * 
 * // Nouvelle réservation restaurant - CLIENT AVEC CHAMBRE
 * await addDailyNotification({
 *   type: 'new',
 *   category: 'restaurant',
 *   restaurantId: 'laTable',
 *   data: {
 *     clientName: 'Marie Martin',
 *     email: 'marie@example.com',
 *     date: '2024-11-20',
 *     time: '19:30',
 *     service: 'Dîner',
 *     guests: 4,
 *     hotelReservationNumber: '202511008', // ← IMPORTANT : numéro de réservation de chambre
 *     hotelReservationId: 'abc123' // ← OPTIONNEL : ID Firebase de la réservation
 *   }
 * });
 * 
 * // Nouvelle réservation restaurant - CLIENT EXTÉRIEUR (sans chambre)
 * await addDailyNotification({
 *   type: 'new',
 *   category: 'restaurant',
 *   restaurantId: 'cafe',
 *   data: {
 *     clientName: 'Paul Durand',
 *     email: 'paul@example.com',
 *     date: '2024-11-20',
 *     time: '12:30',
 *     service: 'Déjeuner',
 *     guests: 2
 *     // ← PAS de hotelReservationNumber pour les clients extérieurs
 *   }
 * });
 * 
 * // Modification réservation restaurant - CLIENT AVEC CHAMBRE
 * await addDailyNotification({
 *   type: 'modified',
 *   category: 'restaurant',
 *   restaurantId: 'laTable',
 *   data: {
 *     clientName: 'Marie Martin',
 *     email: 'marie@example.com',
 *     date: '2024-11-20',
 *     time: '19:30',
 *     service: 'Dîner',
 *     guestsOld: 4, // ← Pour les modifications : ancien nombre
 *     guestsNew: 6, // ← Pour les modifications : nouveau nombre
 *     hotelReservationNumber: '202511008' // ← Si client avec chambre
 *   }
 * });
 * 
 * // Nouvelle réservation service
 * await addDailyNotification({
 *   type: 'new',
 *   category: 'service',
 *   data: {
 *     serviceName: 'Massage (Amma)',
 *     guests: 1,
 *     clientName: 'Paul Bernard',
 *     date: '2024-11-21',
 *     time: '14:00',
 *     reservationNumber: 'R-2024-001'
 *   }
 * });
 * 
 * // Modification horaires train
 * await addDailyNotification({
 *   type: 'modified',
 *   category: 'train',
 *   data: {
 *     clientName: 'Sophie Dubois',
 *     reservationNumber: 'R-2024-001',
 *     checkIn: '2024-11-20',
 *     checkOut: '2024-11-22',
 *     arrivalOld: '15:28 → 16:47',
 *     arrivalNew: '17:32 → 19:14',
 *     departureOld: '16:30 → 18:00',
 *     departureNew: '18:55 → 20:15'
 *   }
 * });
 */